// react
import { createContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// utils
import PropTypes from 'prop-types';
import axios from '../utils/axios';
import { isValidToken, setSession, identifyInPostHogFromAccess } from '../utils/jwt';
import { getAttributionPayload } from '../utils/utmAttribution';
// actions
import * as actionsAuth from '../redux/auth/actions';

// ----------------------------------------------------------------------

const AuthContext = createContext({
  method: 'jwt',
  login: () => Promise.resolve(),
  loginWithGoogle: () => Promise.resolve(),
  loginFromTokens: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const authstate = useSelector((state) => state.auth);

  const identifyInPostHog = (user) => {
    if (typeof window !== 'undefined' && window.posthog?.identify && user?.email) {
      window.posthog.identify(user.email, {
        email: user.email,
        name: user.name,
        user_id: user.id,
      });
    }
  };

  // Load user from stored tokens. Called on mount.
  const initUser = async () => {
    try {
      const access = localStorage.getItem('access');
      if (!(access && isValidToken(access))) {
        return;
      }
      // Identify in PostHog straight from the JWT claims so analytics knows
      // who this is even before /user/me/ resolves.
      identifyInPostHogFromAccess(access);
      const response = await axios.get('/user/me/');
      const user = response.data;
      if (user && user.email) {
        dispatch(actionsAuth.setUser(user));
        identifyInPostHog(user);
      }
    } catch (e) {
      // Token invalid or network error — axios interceptor handles refresh.
      // If we still failed, user remains unauthenticated.
      console.error(e.message || e);
    } finally {
      dispatch(actionsAuth.initialize());
    }
  };

  useEffect(() => {
    initUser();
  }, []);

  const fetchAndStoreUser = async () => {
    const response = await axios.get('/user/me/');
    const user = response.data;
    dispatch(actionsAuth.setUser(user));
    dispatch(actionsAuth.initialize());
    identifyInPostHog(user);
    return user;
  };

  const login = async (email, password) => {
    const response = await axios.post('/token/obtain/', { email, password });
    if ('ERROR' in response.data) {
      throw new Error('ocurrio un error');
    }
    const { access, refresh } = response.data;
    setSession(access, refresh);
    await fetchAndStoreUser();
  };

  const loginWithGoogle = async (credential, extraData = {}, { hydrate = true } = {}) => {
    // Attribution from localStorage feeds the default signup_source /
    // signup_campaign. extraData overrides it so explicit callers
    // (e.g. RegaloLecturaTarot landing) keep precedence.
    const attribution = getAttributionPayload();
    const response = await axios.post('/user/google_login/', {
      credential,
      ...attribution,
      ...extraData,
    });
    if ('response' in response.data) {
      throw new Error(response.data.response);
    }
    const { access, refresh } = response.data;
    setSession(access, refresh);
    if (hydrate) {
      await fetchAndStoreUser();
    }
    return response.data;
  };

  // Pair of tokens → authenticated. Use after any endpoint that already
  // issued a JWT (e.g. /user/register/) to avoid a second round-trip
  // through /token/obtain/. Pass hydrate:false to delay the redux state
  // update — useful when callers need to make authenticated calls before
  // GuestGuard reacts to the new session.
  const loginFromTokens = async (access, refresh, { hydrate = true } = {}) => {
    setSession(access, refresh);
    if (hydrate) {
      await fetchAndStoreUser();
    }
  };

  const logout = () => {
    setSession(null);
    dispatch(actionsAuth.logoutRequest());
    if (typeof window !== 'undefined' && window.posthog?.reset) {
      window.posthog.reset();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...authstate,
        method: 'jwt',
        login,
        loginWithGoogle,
        loginFromTokens,
        logout,
        // Re-fetch /user/me/ and push into redux. Callers use this after
        // an async server-side state change (e.g. a PayPal subscription
        // that grants a perk via webhook) to refresh the client's view.
        refreshUser: fetchAndStoreUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
